import { useContext, useCallback, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { checkIsResponseSuccess } from '../utils/api';
// Hook for using auth context
const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    const { service, user, setUser, isAuthenticated, isLoaded, tokens, updateAuthState, clearAuthState, config, sharedUser, isSharedMode, setSharedUser, setIsSharedMode, } = context;
    // Login method
    const login = useCallback(async (urlParams = {
        email: '',
        password: '',
    }) => {
        const { email, password, ...otherUrlParams } = urlParams;
        const response = await service.loginCall(email, password, otherUrlParams);
        if (checkIsResponseSuccess(response.status)) {
            let user = null;
            let deleted_session = null;
            if (config.appPrefix === 'pub') {
                user = response.data;
            }
            else {
                user = response.data.user;
                deleted_session = response.data.deleted_session;
            }
            const headers = {
                authorization: response.headers.authorization,
                refresh: response.headers['x-refresh-token'],
            };
            updateAuthState({
                user: user,
                headers: headers,
                ...(deleted_session && { deleted_session }),
            });
            service.setSession({
                userId: user?.id,
                headers: headers,
            });
        }
        return response;
    }, [service, updateAuthState]);
    // Logout method
    const logout = useCallback(async (urlParams = {}) => {
        const session = service.getSession();
        const latestAuthorization = session.headers?.authorization || tokens?.authorization || '';
        const latestRefresh = session.headers?.refresh || tokens?.refresh || '';
        if (!latestAuthorization || !latestRefresh) {
            service.removeSession();
            clearAuthState();
            return null;
        }
        const response = await service.logoutCall({
            status: user?.status || 0,
            headers: {
                authorization: latestAuthorization,
                refresh: latestRefresh,
            },
            ...urlParams,
        });
        service.removeSession();
        clearAuthState();
        service.setSession({});
        return response;
    }, [service, clearAuthState, tokens, user?.status]);
    const loginBecome = useCallback(async (params, onSuccess) => {
        const { token, refreshToken } = params;
        if (!token || !refreshToken) {
            throw new Error('Token and refreshToken are required for become login');
        }
        const authToken = `Bearer ${token}`;
        const refreshTokenWithBearer = `Bearer ${refreshToken}`;
        await service.loginBecome({ token: authToken, refreshToken: refreshTokenWithBearer }, async ({ user, headers }) => {
            updateAuthState({
                user: user,
                headers: {
                    authorization: headers.Authorization,
                    refresh: headers['x-refresh-token'],
                },
            });
            if (onSuccess) {
                await onSuccess();
            }
        });
    }, [service, updateAuthState]);
    const loginBecomePub = useCallback(async (params, onSuccess) => {
        const { apiKey } = params;
        if (!apiKey) {
            throw new Error('API key is required for PUB become login');
        }
        await service.loginBecomePub({ apiKey }, async ({ user }) => {
            updateAuthState({
                user: user,
                headers: {
                    authorization: '',
                    refresh: '',
                },
            });
            if (onSuccess) {
                await onSuccess();
            }
        });
    }, [service, updateAuthState]);
    const loginShared = async (id, onSuccess, onDeletedSession) => {
        if (!user || !tokens.authorization || !tokens.refresh) {
            throw new Error('No main user session found');
        }
        await service.loginShared(id, async ({ user: sharedUser, headers: sharedHeaders, sharedUserId, deleted_session, }) => {
            if (deleted_session?.id) {
                if (onDeletedSession) {
                    onDeletedSession(deleted_session);
                }
                else {
                    console.warn('You have reached the limit on the number of concurrent sessions.', 'Session ID:', deleted_session.id);
                }
            }
            await setUser(sharedUser);
            setSharedUser({
                sharedUserId: sharedUserId,
                sharedTokens: sharedHeaders,
            });
            setIsSharedMode(true);
            if (onSuccess) {
                await onSuccess();
            }
        });
    };
    const logoutShared = async () => {
        const session = service.getSession();
        const mainTokensAuthorization = session.headers?.authorization || tokens?.authorization || '';
        const mainTokensRefresh = session.headers?.refresh || tokens?.refresh || '';
        if (!mainTokensAuthorization || !mainTokensRefresh) {
            // No main tokens - full logout needed
            console.warn('No main user tokens found, performing full logout');
            service.removeSession();
            clearAuthState();
            return null;
        }
        const mainTokens = {
            authorization: mainTokensAuthorization,
            refresh: mainTokensRefresh,
        };
        const result = await service.logoutShared(mainTokens);
        if (!result) {
            console.warn('Failed to logout from shared session, staying in shared mode');
            return null;
        }
        const { user: mainUser, response } = result;
        updateAuthState({
            user: mainUser,
            headers: mainTokens,
        });
        setSharedUser(null);
        setIsSharedMode(false);
        return response;
    };
    const onRefreshError = useCallback((error) => {
        console.error('Failed to refresh token:', error);
        void logout();
    }, [logout]);
    // Start refresh interval for main user tokens
    // Cleanup stops the interval on unmount or when dependencies change
    useEffect(() => {
        if (tokens.refresh && tokens.authorization) {
            return service.refresh(tokens.authorization, tokens.refresh, onRefreshError);
        }
    }, [user?.id]);
    // Start refresh interval for shared user tokens
    // Cleanup stops the interval on unmount or when exiting shared mode
    useEffect(() => {
        if (isSharedMode &&
            sharedUser?.sharedTokens?.authorization &&
            sharedUser?.sharedTokens?.refresh) {
            return service.refreshShared(sharedUser.sharedTokens.authorization, sharedUser.sharedTokens.refresh, onRefreshError);
        }
    }, [
        isSharedMode,
        sharedUser?.sharedUserId,
        sharedUser?.sharedTokens?.authorization,
        sharedUser?.sharedTokens?.refresh,
    ]);
    return {
        isAuthenticated,
        isLoaded,
        user,
        setUser,
        tokens,
        isSharedMode,
        sharedUser,
        login,
        logout,
        loginBecome,
        loginBecomePub,
        loginShared,
        logoutShared,
    };
};
export default useAuth;
//# sourceMappingURL=useAuth.js.map